import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowRight, Bookmark, RefreshCw, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BASE_URL,
  questionsAPI,
  subjectivequizApi,
} from "../../../api/api";
import api from "../../../utils/api";
import "./TestPanel.css";

const SubjectiveTestPanel = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [loading, setLoading] = useState(true);
  const [leaveWarningShown, setLeaveWarningShown] = useState(false);

  const chooseQuizFromList = (quizList = []) => {
    if (!Array.isArray(quizList)) return null;
    return quizList.find(
      (q) => Array.isArray(q.test_question) && q.test_question.length
    );
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [quizRes, questionRes] = await Promise.all([
          api.get(subjectivequizApi),
          axios.get(`${BASE_URL}${questionsAPI}`),
        ]);

        const quizData = Array.isArray(quizRes?.data)
          ? chooseQuizFromList(quizRes.data)
          : chooseQuizFromList(quizRes.data?.data ?? quizRes.data);

        if (!quizData) {
          toast.warn("No active subjective quiz with questions found.");
          setQuiz(null);
          setQuestions([]);
          return;
        }

        const allQuestions = Array.isArray(questionRes?.data)
          ? questionRes.data
          : questionRes.data?.data ?? [];

        const filtered = quizData.test_question
          .map((id) => allQuestions.find((q) => Number(q.id) === Number(id)))
          .filter(Boolean);

        if (!filtered.length) {
          toast.warn("No matching questions found for the selected quiz.");
        }

        setQuiz(quizData);
        setQuestions(filtered);
        setCurrentIndex(0);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load quiz/questions. Check console for details.");
        setQuiz(null);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const currentQuestion = useMemo(
    () => questions[currentIndex],
    [questions, currentIndex]
  );

  const handleFileChange = (qid, files) => {
    setAnswers((prev) => ({ ...prev, [qid]: files }));
  };

  const handleClear = () => {
    const qid = currentQuestion?.id;
    if (!qid) return;
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[qid];
      return updated;
    });
    toast.info("Response cleared");
  };

  const handleMarkAndNext = () => {
    const qid = currentQuestion?.id;
    if (qid) {
      setFlagged((prev) => ({ ...prev, [qid]: true }));
    }
    toast.success("Marked and moving to next");
    moveNext();
  };

  const submitAllAnswers = async () => {
    const studentData = JSON.parse(
      sessionStorage.getItem("studentSession") || "{}"
    );
    const student_id = studentData?.id;

    if (!student_id) return;

    const submissions = Object.keys(answers).map(async (qid) => {
      if (answers[qid]?.length > 0) {
        const formData = new FormData();
        formData.append("test_id", quiz?.id ?? "");
        formData.append("question_id", qid);
        formData.append("student_id", student_id);

        Array.from(answers[qid]).forEach((file) => {
          formData.append("files", file);
        });

        try {
          await api.post(`${BASE_URL}/subjective-test/submit`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          console.error("Upload failed for QID", qid, err);
        }
      }
    });

    await Promise.all(submissions);
    toast.success("All answers submitted. Redirecting...");
    setTimeout(() => {
      navigate("/dashboard");
    }, 2000);
  };

  const handleSaveAndNext = async () => {
    const qid = currentQuestion?.id;
    const studentData = JSON.parse(
      sessionStorage.getItem("studentSession") || "{}"
    );
    const student_id = studentData?.id;

    if (!student_id) {
      toast.error("No student session found!");
      return;
    }

    if (qid && answers[qid]?.length > 0) {
      try {
        const formData = new FormData();
        formData.append("test_id", quiz?.id ?? "");
        formData.append("question_id", qid);
        formData.append("student_id", student_id);

        Array.from(answers[qid]).forEach((file) => {
          formData.append("files", file);
        });

        await api.post(`${BASE_URL}/subjective-test/submit`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.success("Answer uploaded successfully. Moving to next question.");
      } catch (error) {
        console.error("Upload failed:", error);
        toast.error("Failed to upload file. Please try again.");
      }
    } else {
      toast.info("No file uploaded — moving to next");
    }

    moveNext();
  };

  const moveNext = () => {
    setCurrentIndex((prev) => {
      if (prev < questions.length - 1) return prev + 1;
      toast.info("You have reached the last question");
      return prev;
    });
  };

  const movePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goToQuestion = (i) => {
    if (i < 0 || i >= questions.length) return;
    setCurrentIndex(i);
  };

  const RenderHTML = ({ html }) => (
    <span dangerouslySetInnerHTML={{ __html: html ?? "" }} />
  );

  // 🚨 Locking tab & page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      if (!leaveWarningShown) {
        toast.warn(
          "Leaving the page is not allowed! Attempt again to auto-submit."
        );
        setLeaveWarningShown(true);
      } else {
        submitAllAnswers();
      }
      return "";
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!leaveWarningShown) {
          toast.warn(
            "Switching tabs is not allowed! Attempt again to auto-submit."
          );
          setLeaveWarningShown(true);
        } else {
          submitAllAnswers();
        }
      }
    };

    const handleContextMenu = (e) => e.preventDefault(); // disable right click
    const handleKeyDown = (e) => {
      // block Ctrl+T, Ctrl+N, Ctrl+Shift+N, Ctrl+W
      if (
        (e.ctrlKey && ["t", "n", "w"].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "n")
      ) {
        e.preventDefault();
        toast.warn("Opening new tabs/windows is disabled in this test!");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [answers, leaveWarningShown]);

  return (
    <div className="mock-test-panel-container">

      {/* Header */}
      <header className="mock-test-panel-header">
        <div className="mock-test-panel-header-inner">
          <div className="mock-test-panel-title">
            <h1>{quiz?.test_name ?? "Subjective Test"}</h1>
            <p className="mock-test-panel-subtitle">{quiz?.test_description}</p>
          </div>

          <div className="mock-test-panel-meta">
            <div className="mock-test-panel-progress">
              <div className="mock-test-panel-progress-bar">
                <div
                  className="mock-test-panel-progress-fill"
                  style={{
                    width: questions.length
                      ? `${(Object.keys(answers).length / questions.length) * 100}%`
                      : "0%",
                  }}
                />
              </div>
              <small>
                {questions.length
                  ? `${Object.keys(answers).length}/${questions.length} answered`
                  : "0/0"}
              </small>
            </div>
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="mock-test-panel-body">
        <aside className="mock-test-panel-sidebar" aria-label="Question list">
          <h3 className="mock-test-panel-sidebar-title">Questions</h3>
          <div className="mock-test-panel-question-list">
            {loading ? (
              <div className="mock-test-panel-loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="mock-test-panel-empty">No questions available</div>
            ) : (
              questions.map((q, i) => {
                const answered = !!answers[q.id];
                const isFlagged = !!flagged[q.id];
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(i)}
                    className={`mock-test-panel-qnav ${
                      i === currentIndex ? "active" : ""
                    } ${answered ? "answered" : ""} ${isFlagged ? "flagged" : ""}`}
                    aria-label={`Question ${i + 1}${answered ? " answered" : ""}${
                      isFlagged ? " flagged" : ""
                    }`}
                    title={`Question ${i + 1}`}
                  >
                    <div className="mock-test-panel-qnum">{i + 1}</div>
                    <div className="mock-test-panel-qbadge">
                      {isFlagged ? <Bookmark size={12} /> : answered ? "✓" : ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="mock-test-panel-sidebar-actions">
            <button
              className="mock-test-panel-sidebar-prev"
              onClick={movePrev}
              disabled={currentIndex === 0}
            >
              Prev
            </button>
            <button
              className="mock-test-panel-sidebar-next"
              onClick={moveNext}
              disabled={currentIndex >= questions.length - 1}
            >
              Next
            </button>
          </div>
        </aside>

        <section className="mock-test-panel-question-area">
          {loading ? (
            <div className="mock-test-panel-loading-large">Loading...</div>
          ) : !currentQuestion ? (
            <div className="mock-test-panel-empty-large">No question to display</div>
          ) : (
            <article className="mock-test-panel-question-card">
              <header className="mock-test-panel-question-head">
                <div className="mock-test-panel-qmeta">
                  <span className="mock-test-panel-subject-name">
                    {currentQuestion.subject_name}
                  </span>
                  <span className="mock-test-panel-topic-name">
                    {currentQuestion.topic_name}
                  </span>
                </div>
                <div className="mock-test-panel-qindex">
                  Q {currentIndex + 1} / {questions.length}
                </div>
              </header>

              <div className="mock-test-panel-qtext">
                <RenderHTML html={currentQuestion.question_text} />
              </div>

              <div className="mock-test-panel-upload">
                <label className="mock-test-panel-upload-label">
                  <Upload size={18} /> Attach Your Answer
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) =>
                    handleFileChange(currentQuestion.id, e.target.files)
                  }
                />
                {answers[currentQuestion.id] && (
                  <div className="mock-test-panel-upload-preview">
                    {Array.from(answers[currentQuestion.id]).map((file, idx) => (
                      <p key={idx}>{file.name}</p>
                    ))}
                  </div>
                )}
              </div>

              <footer className="mock-test-panel-actions">
                <button
                  className="mock-test-panel-btn clear"
                  onClick={handleClear}
                >
                  <RefreshCw size={16} /> Clear Response
                </button>

                <button
                  className="mock-test-panel-btn mark"
                  onClick={handleMarkAndNext}
                >
                  <Bookmark size={16} /> Mark & Next
                </button>

                <button
                  className="mock-test-panel-btn save"
                  onClick={handleSaveAndNext}
                >
                  Save & Next <ArrowRight size={16} />
                </button>
              </footer>
            </article>
          )}
        </section>
      </main>
    </div>
  );
};

export default SubjectiveTestPanel;
